import { qs, qsa, on, delegate } from '../lib/dom.js';

/**
 * Horizontal scrolling for the catalog's quick-filter strip on pointer devices.
 *
 * The strip hides its scrollbar by design, which was fine while it carried a
 * handful of models. Once it started listing every manufacturer, a mouse user
 * had no way to reach the overflow at all: no scrollbar, no touch, and a wheel
 * scrolls the page rather than the row - the client reported the list simply
 * stopping at whatever fit (1561px of brands unreachable at 1440px wide).
 *
 * Two ways in, both pointer-only, neither touching the touch behaviour:
 *   - arrow buttons at the card's edges, revealed only while the track really
 *     overflows and hidden again at each end;
 *   - the mouse wheel, translated to horizontal movement while the cursor is
 *     over the strip, yielding back to the page at either end so the page never
 *     feels stuck.
 *
 * The strip is re-rendered wholesale on every filter change, so the handlers are
 * delegated on document and `syncScrollStrips()` is called again after each swap.
 */

const EDGE = 2; // px tolerance for "at the end" - scrollLeft can be fractional

const trackOf = (strip) => qs('.ap-cat-models__track', strip);

/** Show/hide each strip's arrows to match its current scroll position. */
export function syncScrollStrips(root = document) {
  const scope = root && root.querySelectorAll ? root : document;
  const strips = scope.matches && scope.matches('.ap-cat-models')
    ? [scope]
    : qsa('.ap-cat-models', scope);
  strips.forEach((strip) => {
    const track = trackOf(strip);
    if (!track) return;
    const overflow = track.scrollWidth - track.clientWidth;
    const prev = qs('[data-strip-nav="prev"]', strip);
    const next = qs('[data-strip-nav="next"]', strip);
    const scrollable = overflow > EDGE;
    if (prev) prev.hidden = !scrollable || track.scrollLeft <= EDGE;
    if (next) next.hidden = !scrollable || track.scrollLeft >= overflow - EDGE;
  });
}

export function initScrollStrips() {
  // Arrows. Scroll by most of a viewport so a click makes obvious progress but
  // still leaves a pill or two of context.
  delegate(document, 'click', '[data-strip-nav]', (e, btn) => {
    e.preventDefault();
    const strip = btn.closest('.ap-cat-models');
    const track = strip && trackOf(strip);
    if (!track) return;
    const step = Math.max(200, Math.round(track.clientWidth * 0.8));
    track.scrollBy({ left: btn.dataset.stripNav === 'prev' ? -step : step, behavior: 'smooth' });
  });

  // Wheel → horizontal. Needs passive:false to preventDefault, which the dom
  // helper allows only when the option is passed explicitly.
  on(document, 'wheel', (e) => {
    const track = e.target && e.target.closest ? e.target.closest('.ap-cat-models__track') : null;
    if (!track) return;
    // A trackpad's horizontal gesture already works - don't fight it.
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
    const overflow = track.scrollWidth - track.clientWidth;
    if (overflow <= EDGE) return;
    const atStart = track.scrollLeft <= EDGE;
    const atEnd = track.scrollLeft >= overflow - EDGE;
    // At either end, hand the gesture back to the page instead of trapping it.
    if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;
    e.preventDefault();
    track.scrollLeft += e.deltaY;
  }, { passive: false });

  // Keep the arrows in step. `scroll` doesn't bubble, so listen in the capture
  // phase rather than binding per-track (the strip is replaced on every filter
  // change, and those bindings would be lost).
  on(document, 'scroll', (e) => {
    const t = e.target;
    if (t && t.classList && t.classList.contains('ap-cat-models__track')) {
      syncScrollStrips(t.closest('.ap-cat-models'));
    }
  }, true);

  on(window, 'resize', () => syncScrollStrips());
  syncScrollStrips();
}
